import {
  LabsAndDiagnosticsTemplate,
  type LabsTemplatePanel,
  type LabsTemplateRow,
} from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import { getProviderFacilityPdfEntry } from './pdfOverride';
import type { RawSourceMetadata } from './_shared';

type LabResultsDataKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_DATA; type: DocumentType.LAB_RESULTS }
>;

// sourceMetadata lives on individual rows, not the panel itself.
interface RawLabRow extends LabsTemplateRow {
  dateUploaded?: string;
  sourceMetadata?: RawSourceMetadata;
  patientId?: string;
  name?: string;
  labCodes?: string[];
  unit?: string;
  performerPractitionerRoleIdReferences?: string[];
  interpreterPractitionerRoleIdReferences?: string[];
}

interface RawLabPanel extends Omit<LabsTemplatePanel, 'rows'> {
  labPanel: string | null;
  rows: RawLabRow[];
}

export interface LabResultsDataQueryData {
  labPanels: RawLabPanel[];
}

export const adaptLabResultsData = (
  data: LabResultsDataQueryData,
  key: LabResultsDataKey,
): DocumentEntry | undefined => {
  const panels = data?.labPanels ?? [];
  if (panels.length === 0) return undefined;

  // sourceMetadata is per-row, so flatten all rows across panels to find the URL.
  const allRows = panels.flatMap((p) => p.rows);
  const pdf = getProviderFacilityPdfEntry(key.sourceType, allRows, {
    title: key.name,
  });
  if (pdf) return pdf;

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: `lab-panel-${key.labPanelId}`,
    render: (ctx) => (
      <LabsAndDiagnosticsTemplate
        panels={panels}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightPanelId={key.labPanelId}
      />
    ),
  };
};
